import React, { useEffect, useState, useMemo, useCallback } from 'react'
import {
  Container,
  Typography,
  Grid,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions, Chip, Tooltip
} from '@material-ui/core'
import { ArrowLeftIcon } from 'asterX-UI/Icons'
import { LoaderBackdrop } from 'asterX-UI/Loaders'
import { HOME_PATH } from 'routes/constants.routes'
import { useRoutes } from 'lib/hooks'
import { useHomeStore } from 'context/HomeStore'
import { CookieService, formatDateTime } from 'lib/utils'
import { QRCodeSVG } from 'qrcode.react'
import { GRAY_COLORS, SUCCESS_COLORS } from 'asterX-UI'
import { InfoModal } from 'asterX-UI/Modals'
import StandardTable from 'asterX-UI/Table/StandardTable'
import { InfoOutlined } from '@material-ui/icons'
import AsterChip from '../../asterX-UI/Chip/AsterChip'
import { useDocumentStyles } from './document.styles'
import SignerCards from './components/SignerCards'


const DocumentPage = () => {
  const router = useRoutes()
  const classes = useDocumentStyles()

  const {
    homeStore: { loading, isMobile, document, id },
    homeStoreFunc: { getQrBySignerId, getDocumentLink, sendSms, postContractId,getResignCount }
  } = useHomeStore()

  const [modalOpen, setModalOpen] = useState(null)
  const [docs, setDocs] = useState([])
  const [hoveredSignerIndex, setHoveredSignerIndex] = useState(null)

  const currentSignerIndex = useMemo(() => Number(document?.currentSignerIndex || 0), [document])
  const totalSigners = useMemo(() => document?.signers?.length || 0, [document])
  const allSigned = useMemo(() => currentSignerIndex >= totalSigners, [currentSignerIndex, totalSigners])

  const currentSigner = useMemo(() => document?.signers?.[currentSignerIndex] || {}, [document, currentSignerIndex])
  const [pdfModalOpen, setPdfModalOpen] = useState(false)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [resignCount, setResignCount] = useState(0)
  const resignLabel = loading ? 'Загрузка…' : `Переподписаний: ${resignCount}`;
  const resignColor = resignCount === 0 ? 'default' : resignCount < 3 ? 'primary' : 'secondary';

  useEffect(() => {
    if (Array.isArray(document?.documents)) {
      setDocs(document.documents)
    }
  }, [document])

  const openQr = useCallback(async (id) => {
    const res = await getQrBySignerId(id)
    if (res?.data?.qr) setModalOpen(res.data.qr)
  }, [getQrBySignerId])

  // const handleDeleteButton = useCallback(
  //   async item => {
  //     item.active = false
  //     await deleteDocument(item.id)
  //   },
  //   [deleteDocument]
  // )

  const openPdfModal = useCallback((url) => {
    setPdfUrl(url)
    setPdfModalOpen(true)
  }, [])

  const handleRowClick = useCallback(
    async row => {
      const btnCell = row.find(cell => cell?.props?.isButton)
      const docId = btnCell?.props?.id
      if (!docId) return


      try {
        const res = await getDocumentLink(docId)
        const url = res?.data?.link
        if (url) {
          openPdfModal(url, docId)
        } else {
          console.error('Ссылка на PDF не пришла')
        }
      } catch (err) {
        console.error('Ошибка при получении PDF ссылки', err)
      }
    },
    [docs, getDocumentLink, openPdfModal]
  )

  const rows = useMemo(() => {
    const activeSignerIndex = hoveredSignerIndex !== null ? hoveredSignerIndex : currentSignerIndex
    return docs
      .filter(doc => doc.isActive)
      .map(item => {
        const isDocOfActiveSigner = document?.signers?.[activeSignerIndex]?.documentCodes?.includes(item.code)
        const isHovered = hoveredSignerIndex !== null && isDocOfActiveSigner

        let borderColor = 'transparent'
        let opacity = 0.5

        if (allSigned) {
          borderColor = 'transparent'
          opacity = 1
        } else if (isDocOfActiveSigner) {
          if (isHovered) {
            borderColor = '#1976d2'
          } else {
            const status = document?.currentStatus?.toLowerCase()
            borderColor = status === 'accessed' ? GRAY_COLORS[500] : 'black'
          }
          opacity = 1
        }

        return {
          style: {
            borderLeft: `2px solid ${borderColor}`,
            borderRight: `2px solid ${borderColor}`,
            opacity
          },
          values: [
            (
              <Box
                key={item.id}
                isButton
                id={item.id}
                style={{
                  color: isDocOfActiveSigner ? SUCCESS_COLORS[600] : GRAY_COLORS[500],
                  cursor: 'pointer'
                }}
              >
                {item.name || '-'}
              </Box>
            ),
            formatDateTime(item.createdAt),
            <AsterChip
              size="small"
              label={item.managerRead ? 'Проверено' : 'Ожидает'}
              variant="outlined"
              sx={{ borderRadius: '6px', fontSize: '12px' }}
              color={item.managerRead ? 'greenOut' : 'yellowOut'}
            />
          ]
        }
      })
  }, [docs, document, currentSignerIndex, hoveredSignerIndex, allSigned])

  useEffect(() => {
    if (!CookieService.getCookie('redirectToContractId') && document?.loanAppId && id) {
      CookieService.setCookie('redirectToCreditFlowByContract_ID', id)
    }
  }, [document, id])
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!id) { setResignCount(0); return; }
      try {
        const res = await getResignCount(id);
        const value = typeof res === 'number' ? res
          : (res?.data ?? 0);
        if (!cancelled) setResignCount(value);
      } catch {
        if (!cancelled) setResignCount(0);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  return (
    <Container style={{ marginBottom: 50, marginTop: 32 }}>
      <LoaderBackdrop loading={loading} />
      <Button
        style={{ padding: 0, marginBottom: 16 }}
        onClick={() => {
          CookieService.deleteCookieEverywhere('redirectToContractId')
          CookieService.deleteCookieEverywhere('redirectToCreditFlowByContract_ID')
          router.goTo(HOME_PATH)
        }}
      >
        <ArrowLeftIcon /> Назад
      </Button>

      <Box style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <Typography className={classes.title}>Номер контракта № {id || '-'}</Typography>

        <div
          style={{ display: 'flex', marginLeft: 'auto', gap: '20px', justifyContent: 'center', alignItems: 'center' }}
        >
          {resignCount > 0 && (
            <Tooltip title="Сколько раз запрашивали переподписание документов">
              <Chip
                icon={<InfoOutlined fontSize="small" />}
                label={resignLabel}
                color={resignColor}
                variant="outlined"
                size="small"
                style={{ fontWeight: 600 }}
              />
            </Tooltip>
          )}

          {currentSigner.signerType === 'COMPANY' && (
            <Button
              variant="outlined"
              style={{ color: SUCCESS_COLORS[600] }}
              onClick={() => openQr(document?.signers[document?.currentSignerIndex].id)}
            >
              Показать QR
            </Button>
          )}
          {document?.canSendSms !== null && (
            <>
              {currentSignerIndex === 0 && document?.canSendSms ? (
                <Button variant="outlined" style={{ color: SUCCESS_COLORS[600] }} onClick={() => sendSms(id)}>
                  Отправить СМС
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  style={{ color: SUCCESS_COLORS[600], marginLeft: 'auto' }}
                  onClick={() => postContractId(currentSigner?.id)}
                >
                  Отправить партнёру
                </Button>
              )}
            </>
          )}
        </div>
      </Box>

      <Grid item className={classes.workList}>
        <Grid item xs={12} sm={12} md={4}>
          <SignerCards
            signers={document?.signers || []}
            currentSignerIndex={currentSignerIndex}
            hoveredSignerIndex={hoveredSignerIndex}
            setHoveredSignerIndex={setHoveredSignerIndex}
          />
        </Grid>
        {!isMobile && (
          <Grid item xs={12} md={8}>
            <StandardTable
              tableData={{
                columns: ['Название документа', 'Дата создания', 'Статус'],
                rows
              }}
              tableStyles={{ borderRadius: 12, boxShadow: 'none' }}
              onRowClick={handleRowClick}
            />
          </Grid>
        )}
      </Grid>

      <InfoModal
        open={modalOpen !== null}
        title={`QR для подписания контракта ${id || '-'}`}
        onClose={() => setModalOpen(null)}
        btnText="Закрыть"
      >
        {modalOpen && (
          <Box display="flex" justifyContent="center" alignItems="center" width="100%">
            <QRCodeSVG value={modalOpen} size={200} level="H" />
          </Box>
        )}
      </InfoModal>

      <Dialog open={pdfModalOpen} onClose={() => setPdfModalOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>Просмотр документа</DialogTitle>
        <DialogContent style={{ padding: 0, height: '80vh' }}>
          <iframe src={pdfUrl} title="PDF" width="100%" height="100%" style={{ border: 'none' }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => window.open(pdfUrl, '_blank', 'noopener,noreferrer')}>Открыть в новом окне</Button>
          <Button onClick={() => setPdfModalOpen(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

export default DocumentPage;
